<template>
<div>
    <v-card>
        <v-row>
            <v-card-text>
                <v-divider class="mt-2 mb-4" />
                <v-row class="mb-0">
                    <v-col cols="12">
                        <v-card outlined class="v-card-outline-primary" elevation="3">
                            <v-card-title>
                                <h5 class="primary--text">{{ $t("fleet.roads.addRoad") }}</h5>
                            </v-card-title>
                            <v-card-text>
                                <v-alert
                                  type="info"
                                  text
                                  dense
                                  class="mb-3"
                                >
                                  {{ $t('fleet.roads.roadRequirements') }}
                                </v-alert>
                                <v-row class="mb-3">
                                    <v-col cols="6" class="text-center">
                                        <h4 class="primary--text">{{ $t('vehicle.load') }}</h4>
                                    </v-col>
                                    <v-col cols="6" class="text-center">
                                        <h4 class="primary--text">{{ $t('vehicle.unload') }}</h4>
                                    </v-col>
                                </v-row>

                                <v-row v-for="(item, index) in sortedPoints" :key="index" class="mb-2">
                                    <!-- Kolumna załadunków -->
                                    <v-col cols="6">
                                        <v-card v-if="item.type === 0" outlined elevation="2" class="pa-3">
                                            <v-row>
                                                <v-col cols="10">
                                                    <v-row>
                                                        <v-col class="pt-0">
                                                            <label>{{ $t('fleet.roads.pointTitle') }}</label>
                                                            <LocationInput
                                                                @removeItem="deletePoint(item)"
                                                                v-model="item.location"
                                                                validation
                                                                hideRange
                                                                clearable
                                                                :disabled="item.orderId != null"
                                                            />
                                                        </v-col>
                                                    </v-row>
                                                    <v-row>
                                                        <v-col>
                                                            <DatePicker
                                                                :disabled="item.orderId != null"
                                                                v-model="item.dateTime"
                                                                :label="$t('fleet.roads.dateTitle')"
                                                            />
                                                        </v-col>
                                                    </v-row>
                                                    <v-row>
                                                        <v-col class="d-flex">
                                                            <div class="mr-2">
                                                                <v-btn
                                                                    v-if="canMoveUp(item)"
                                                                    color="primary"
                                                                    depressed
                                                                    small
                                                                    @click="moveUp(item)"
                                                                >
                                                                    <v-icon>uil uil-arrow-up</v-icon>
                                                                </v-btn>
                                                            </div>
                                                            <div>
                                                                <v-btn
                                                                    v-if="canMoveDown(item)"
                                                                    color="primary"
                                                                    depressed
                                                                    small
                                                                    @click="moveDown(item)"
                                                                >
                                                                    <v-icon>uil uil-arrow-down</v-icon>
                                                                </v-btn>
                                                            </div>
                                                        </v-col>
                                                    </v-row>

                                                    <!-- Expansion panel for additional fields -->
                                                    <v-expansion-panels flat class="mt-3">
                                                        <v-expansion-panel>
                                                            <v-expansion-panel-header>
                                                                {{ $t('fleet.roads.details') }}
                                                            </v-expansion-panel-header>
                                                            <v-expansion-panel-content>
                                                                <v-row>
                                                                    <v-col cols="12">
                                                                        <RichTextEditor
                                                                            v-model="item.description"
                                                                            :label="$t('fleet.roads.description')"
                                                                            :disabled="item.orderId != null"
                                                                        />
                                                                    </v-col>
                                                                </v-row>
                                                                <v-row>
                                                                    <v-col cols="6">
                                                                        <v-checkbox
                                                                            v-model="item.showTimeSliders"
                                                                            :label="$t('fleet.roads.setHours')"
                                                                            :disabled="item.orderId != null"
                                                                            @change="onTimeCheckboxChange(item)"
                                                                        />
                                                                    </v-col>
                                                                    <v-col cols="6">
                                                                        <v-checkbox
                                                                            v-model="item.fix"
                                                                            :label="$t('fleet.roads.fix')"
                                                                            :disabled="item.orderId != null"
                                                                        />
                                                                    </v-col>
                                                                </v-row>
                                                                <v-row v-if="item.showTimeSliders">
                                                                    <v-col cols="12">
                                                                        <HourInput
                                                                            v-model="item.timeFrom"
                                                                            :label="$t('fleet.roads.timeFrom')"
                                                                            :disabled="item.orderId != null"
                                                                            :error="getTimeFromError(item)"
                                                                            :error-messages="getTimeFromErrorMessage(item)"
                                                                            @input="item.dirtTime = true"
                                                                        />
                                                                    </v-col>
                                                                </v-row>
                                                                <v-row v-if="item.showTimeSliders">
                                                                    <v-col cols="12">
                                                                        <HourInput
                                                                            v-model="item.timeTo"
                                                                            :label="$t('fleet.roads.timeTo')"
                                                                            :disabled="item.orderId != null"
                                                                            :error="getTimeToError(item)"
                                                                            :error-messages="getTimeToErrorMessage(item)"
                                                                            @input="item.dirtTime = true"
                                                                        />
                                                                    </v-col>
                                                                </v-row>
                                                                <v-row>
                                                                    <v-col cols="6">
                                                                        <v-text-field
                                                                            v-model.number="item.weight"
                                                                            :label="$t('fleet.roads.weight')"
                                                                            type="number"
                                                                            step="0.01"
                                                                            :disabled="item.orderId != null"
                                                                        />
                                                                    </v-col>
                                                                    <v-col cols="6">
                                                                        <v-text-field
                                                                            v-model.number="item.loadMeters"
                                                                            :label="$t('fleet.roads.loadMeters')"
                                                                            type="number"
                                                                            step="0.01"
                                                                            :disabled="item.orderId != null"
                                                                        />
                                                                    </v-col>
                                                                </v-row>
                                                            </v-expansion-panel-content>
                                                        </v-expansion-panel>
                                                    </v-expansion-panels>
                                                </v-col>
                                                <v-col cols="2" class="d-flex flex-column align-center justify-center">
                                                    <v-btn
                                                        :disabled="item.orderId != null"
                                                        color="primary"
                                                        depressed
                                                        @click="switchType(item)"
                                                        :title="$t('fleet.roads.moveToUnload')"
                                                        class="mb-2"
                                                    >
                                                        <v-icon>uil uil-arrow-right</v-icon>
                                                    </v-btn>
                                                    <div v-if="item.orderId!=null" class="pointer" @click="openDetails(item)">
                                                        <v-icon size="25" color="primary">$infoBox</v-icon>
                                                    </div>
                                                </v-col>
                                            </v-row>
                                        </v-card>
                                    </v-col>

                                    <!-- Kolumna rozładunków -->
                                    <v-col cols="6">
                                        <v-card v-if="item.type === 1" outlined elevation="2" class="pa-3">
                                            <v-row>
                                                <v-col cols="2" class="d-flex flex-column align-center justify-center">
                                                    <v-btn
                                                        :disabled="item.orderId != null"
                                                        color="primary"
                                                        depressed
                                                        @click="switchType(item)"
                                                        :title="$t('fleet.roads.moveToLoad')"
                                                        class="mb-2"
                                                    >
                                                        <v-icon>uil uil-arrow-left</v-icon>
                                                    </v-btn>
                                                    <div v-if="item.orderId!=null" class="pointer" @click="openDetails(item)">
                                                        <v-icon size="25" color="primary">$infoBox</v-icon>
                                                    </div>
                                                </v-col>
                                                <v-col cols="10">
                                                    <v-row>
                                                        <v-col class="pt-0">
                                                            <label>{{ $t('fleet.roads.pointTitle') }}</label>
                                                            <LocationInput
                                                                @removeItem="deletePoint(item)"
                                                                v-model="item.location"
                                                                validation
                                                                hideRange
                                                                clearable
                                                                :disabled="item.orderId != null"
                                                            />
                                                        </v-col>
                                                    </v-row>
                                                    <v-row>
                                                        <v-col>
                                                            <DatePicker
                                                                :disabled="item.orderId != null"
                                                                v-model="item.dateTime"
                                                                :label="$t('fleet.roads.dateTitle')"
                                                            />
                                                        </v-col>
                                                    </v-row>
                                                    <v-row>
                                                        <v-col class="d-flex">
                                                            <div class="mr-2">
                                                                <v-btn
                                                                    v-if="canMoveUp(item)"
                                                                    color="primary"
                                                                    depressed
                                                                    small
                                                                    @click="moveUp(item)"
                                                                >
                                                                    <v-icon>uil uil-arrow-up</v-icon>
                                                                </v-btn>
                                                            </div>
                                                            <div>
                                                                <v-btn
                                                                    v-if="canMoveDown(item)"
                                                                    color="primary"
                                                                    depressed
                                                                    small
                                                                    @click="moveDown(item)"
                                                                >
                                                                    <v-icon>uil uil-arrow-down</v-icon>
                                                                </v-btn>
                                                            </div>
                                                        </v-col>
                                                    </v-row>

                                                    <!-- Expansion panel for additional fields -->
                                                    <v-expansion-panels flat class="mt-3">
                                                        <v-expansion-panel>
                                                            <v-expansion-panel-header>
                                                                {{ $t('fleet.roads.details') }}
                                                            </v-expansion-panel-header>
                                                            <v-expansion-panel-content>
                                                                <v-row>
                                                                    <v-col cols="12">
                                                                        <RichTextEditor
                                                                            v-model="item.description"
                                                                            :label="$t('fleet.roads.description')"
                                                                            :disabled="item.orderId != null"
                                                                        />
                                                                    </v-col>
                                                                </v-row>
                                                                <v-row>
                                                                    <v-col cols="6">
                                                                        <v-checkbox
                                                                            v-model="item.showTimeSliders"
                                                                            :label="$t('fleet.roads.setHours')"
                                                                            :disabled="item.orderId != null"
                                                                            @change="onTimeCheckboxChange(item)"
                                                                        />
                                                                    </v-col>
                                                                    <v-col cols="6">
                                                                        <v-checkbox
                                                                            v-model="item.fix"
                                                                            :label="$t('fleet.roads.fix')"
                                                                            :disabled="item.orderId != null"
                                                                        />
                                                                    </v-col>
                                                                </v-row>
                                                                <v-row v-if="item.showTimeSliders">
                                                                    <v-col cols="12">
                                                                        <HourInput
                                                                            v-model="item.timeFrom"
                                                                            :label="$t('fleet.roads.timeFrom')"
                                                                            :disabled="item.orderId != null"
                                                                            :error="getTimeFromError(item)"
                                                                            :error-messages="getTimeFromErrorMessage(item)"
                                                                            @input="item.dirtTime = true"
                                                                        />
                                                                    </v-col>
                                                                </v-row>
                                                                <v-row v-if="item.showTimeSliders">
                                                                    <v-col cols="12">
                                                                        <HourInput
                                                                            v-model="item.timeTo"
                                                                            :label="$t('fleet.roads.timeTo')"
                                                                            :disabled="item.orderId != null"
                                                                            :error="getTimeToError(item)"
                                                                            :error-messages="getTimeToErrorMessage(item)"
                                                                            @input="item.dirtTime = true"
                                                                        />
                                                                    </v-col>
                                                                </v-row>
                                                                <v-row>
                                                                    <v-col cols="6">
                                                                        <v-text-field
                                                                            v-model.number="item.weight"
                                                                            :label="$t('fleet.roads.weight')"
                                                                            type="number"
                                                                            step="0.01"
                                                                            :disabled="item.orderId != null"
                                                                        />
                                                                    </v-col>
                                                                    <v-col cols="6">
                                                                        <v-text-field
                                                                            v-model.number="item.loadMeters"
                                                                            :label="$t('fleet.roads.loadMeters')"
                                                                            type="number"
                                                                            step="0.01"
                                                                            :disabled="item.orderId != null"
                                                                        />
                                                                    </v-col>
                                                                </v-row>
                                                            </v-expansion-panel-content>
                                                        </v-expansion-panel>
                                                    </v-expansion-panels>
                                                </v-col>
                                            </v-row>
                                        </v-card>
                                    </v-col>
                                </v-row>

                                <v-divider class="my-4"></v-divider>

                                <v-row>
                                    <v-col cols="6">
                                        <v-btn :disabled="points.length >=30" color="primary" depressed @click="addPoint" class="ml-auto">
                                            {{ $t('common.add') }}
                                        </v-btn>
                                    </v-col>
                                    <v-col cols="1">
                                        <v-btn :disabled="!canSave" color="primary" depressed @click="saveRoad" class="ml-auto">
                                            {{ $t('common.save') }}
                                        </v-btn>
                                    </v-col>
                                    <v-col cols="1">
                                        <v-btn  depressed @click="cancel" class="ml-auto">
                                            {{ $t('common.cancel') }}
                                        </v-btn>
                                    </v-col>
                                </v-row>

                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
                <v-divider class="mt-2 mb-4" />
            </v-card-text>
        </v-row>
    </v-card>
    <OfferDetailsDialog
      v-if="offerId"
      :value="offerDetailsActive"
      :itemId="offerId"
      :isVehicle="false"
      @closed="closeOfferDetailsDialog"
    />
    <v-dialog
      v-model="dateDialog"
      width="800"
    >
      <v-card>
        <v-card-title>
          {{ $t('fleet.roads.selectFirstDateTitle') }}
        </v-card-title>
        <v-card-text>
            <DatePicker v-model="date" :label="$t('filters.loadDateFrom')" />
        </v-card-text>
        <v-card-actions class="px-6 pb-4 justify-end">
          <v-btn
            outlined
            @click="closeDateDialog"
          >
            {{ $t("languagesSettings.close") }}
          </v-btn>
          <v-btn
            color="primary"
            :disabled="date==null"
            @click="setDate"
          >
            {{ $t("common.save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
</div>
</template>

<script>
import Service from '@/services'
import DatePicker from '@/components/forms/DatePicker'
import LocationInput from '@/components/forms/LocationInput'
import OfferDetailsDialog from '@/views/Market/dialogs/OfferDetailsDialog.vue'
import RichTextEditor from '@/components/forms/RichTextEditor'
import HourInput from '@/components/forms/HourInput'
import * as moment from 'moment'

export default {
  name: 'Road',
  data () {
    return {
      dateDialog: false,
      date: null,
      offerDetailsActive: false,
      offerId: null,
      points: [],
      loading: false,
      isReordering: false
    }
  },
  components: {
    LocationInput,
    DatePicker,
    OfferDetailsDialog,
    RichTextEditor,
    HourInput
  },
  computed: {
    editMode () {
      return this.$route.name === 'EditRoad'
    },
    copyMode () {
      return this.$route.name === 'CopyRoad'
    },
    vehicleId () {
      return this.$route.params.id
    },
    sortedPoints () {
      return [...this.points].sort((a, b) => a.order - b.order)
    },
    canSave () {
      // Walidacja: pierwszy musi być załadunek, min 1 załadunek i min 1 rozładunek
      if (this.points.length === 0) return false

      const sortedByOrder = [...this.points].sort((a, b) => a.order - b.order)
      const hasFirstLoad = sortedByOrder[0].type === 0
      const hasAtLeastOneLoad = this.points.some(point => point.type === 0)
      const hasAtLeastOneUnload = this.points.some(point => point.type === 1)
      const allHaveDates = this.points.every(item => item.dateTime !== null)
      const allHaveLocations = this.points.every(item => item.location.country !== null)

      return hasFirstLoad && hasAtLeastOneLoad && hasAtLeastOneUnload && allHaveDates && allHaveLocations
    }
  },
  watch: {
    points: {
      handler (newPoints) {
        if (!this.isReordering) {
          this.reorderPointsByDate()
        }
      },
      deep: true
    }
  },
  async created () {
    if (this.editMode || this.copyMode) {
      const {
        data
      } = await Service.get('road/GetDetails', {
        RoadId: this.$route.params.roadId
      })
      if (this.copyMode) {
        this.points.forEach((point, index) => {
          point.orderId = null
        })
      }
      this.points = data.places.map(point => ({
        ...point,
        showTimeSliders: !!(point.timeFrom || point.timeTo),
        timeFrom: this.convertTimeSpanToMillis(point.timeFrom),
        timeTo: this.convertTimeSpanToMillis(point.timeTo),
        dirtTime: false
      }))
      if (this.copyMode) {
        this.dateDialog = true
      }
    } else {
      this.addPoint()
    }
  },
  methods: {
    getTimeFromError (item) {
      if (!item.dirtTime) {
        return false
      }
      if (item.timeFrom > item.timeTo) {
        return true
      }
      return false
    },
    getTimeFromErrorMessage (item) {
      if (!item.dirtTime) {
        return []
      }
      if (item.timeFrom > item.timeTo) {
        return this.$t('validation.invalidRange')
      }
      return []
    },
    getTimeToError (item) {
      if (!item.dirtTime) {
        return false
      }
      if (item.timeFrom > item.timeTo) {
        return true
      }
      return false
    },
    getTimeToErrorMessage (item) {
      if (!item.dirtTime) {
        return []
      }
      if (item.timeFrom > item.timeTo) {
        return this.$t('validation.invalidRange')
      }
      return []
    },
    convertTimeSpanToMillis (timeSpan) {
      if (!timeSpan) return null
      // TimeSpan format: "HH:MM:SS" or with days "DD.HH:MM:SS"
      const parts = timeSpan.split(':')
      if (parts.length >= 3) {
        const hours = parseInt(parts[0])
        const minutes = parseInt(parts[1])
        return moment().startOf('day').add(hours, 'hours').add(minutes, 'minutes').valueOf()
      }
      return null
    },
    convertMillisToTimeSpan (millis) {
      if (!millis) return null
      const m = moment(millis)
      const hours = m.hours()
      const minutes = m.minutes()
      const seconds = m.seconds()
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    },
    onTimeCheckboxChange (item) {
      if (!item.showTimeSliders) {
        item.timeFrom = null
        item.timeTo = null
        item.dirtTime = false
      }
    },
    reorderPointsByDate () {
      this.isReordering = true

      // Sortuj punkty chronologicznie po dacie
      const sortedByDate = [...this.points].sort((a, b) => {
        // Punkty bez daty idą na koniec
        if (!a.dateTime && !b.dateTime) return a.order - b.order
        if (!a.dateTime) return 1
        if (!b.dateTime) return -1

        const dateA = new Date(a.dateTime)
        const dateB = new Date(b.dateTime)

        // Sortuj po dacie
        if (dateA.getTime() !== dateB.getTime()) {
          return dateA - dateB
        }

        // Jeśli daty są takie same, zachowaj obecną kolejność
        return a.order - b.order
      })

      // Przypisz nowe wartości order
      sortedByDate.forEach((point, index) => {
        point.order = index
      })

      this.$nextTick(() => {
        this.isReordering = false
      })
    },
    closeDateDialog () {
      this.dateDialog = false
    },
    setDate () {
      this.dateDialog = false
      var el = this.points[0]
      var diff = new Date(this.date.split('T')[0]) - new Date(el.dateTime.split('T')[0])
      this.points.forEach((point, index) => {
        point.dateTime = new Date((new Date(point.dateTime)).getTime() + diff).toISOString()
      })
    },
    openDetails (item) {
      this.$store.commit('market/MARK_SHOW_OFFER', item)
      this.offerId = item.orderId
      this.offerDetailsActive = true
    },
    closeOfferDetailsDialog () {
      this.offerDetailsActive = false
      this.offerId = null
    },
    switchType (item) {
      item.type = item.type === 0 ? 1 : 0
    },
    moveUp (item) {
      const sortedByOrder = [...this.points].sort((a, b) => a.order - b.order)
      const currentIndex = sortedByOrder.findIndex(p => p === item)

      if (currentIndex > 0) {
        const prevItem = sortedByOrder[currentIndex - 1]
        const tempOrder = item.order
        item.order = prevItem.order
        prevItem.order = tempOrder
      }
    },
    moveDown (item) {
      const sortedByOrder = [...this.points].sort((a, b) => a.order - b.order)
      const currentIndex = sortedByOrder.findIndex(p => p === item)

      if (currentIndex < sortedByOrder.length - 1) {
        const nextItem = sortedByOrder[currentIndex + 1]
        const tempOrder = item.order
        item.order = nextItem.order
        nextItem.order = tempOrder
      }
    },
    canMoveUp (item) {
      const sortedByOrder = [...this.points].sort((a, b) => a.order - b.order)
      const currentIndex = sortedByOrder.findIndex(p => p === item)

      if (currentIndex <= 0) {
        return false
      }

      const prevItem = sortedByOrder[currentIndex - 1]
      if (prevItem.dateTime !== null && item.dateTime !== null && prevItem.dateTime === item.dateTime) {
        return true
      }

      return false
    },
    canMoveDown (item) {
      const sortedByOrder = [...this.points].sort((a, b) => a.order - b.order)
      const currentIndex = sortedByOrder.findIndex(p => p === item)

      if (currentIndex >= sortedByOrder.length - 1) {
        return false
      }

      const nextItem = sortedByOrder[currentIndex + 1]
      if (nextItem.dateTime !== null && item.dateTime !== null && nextItem.dateTime === item.dateTime) {
        return true
      }

      return false
    },
    cancel () {
      this.$router.push({
        name: 'DetailsFleetVehicle',
        params: {
          id: this.vehicleId
        }
      })
    },
    validateTimeRanges () {
      for (const point of this.points) {
        if (point.showTimeSliders && point.timeFrom && point.timeTo) {
          if (point.timeFrom > point.timeTo) {
            return false
          }
        }
      }
      return true
    },
    async saveRoad () {
      try {
        // Oznacz wszystkie pola jako "dirt" aby pokazać walidację
        this.points.forEach(point => {
          if (point.showTimeSliders) {
            point.dirtTime = true
          }
        })

        // Walidacja zakresów czasowych
        if (!this.validateTimeRanges()) {
          this.$store.commit('SET_SNACKBAR', {
            text: this.$t('validation.invalidRange'),
            color: 'error'
          })
          return
        }

        const pointsToSend = this.points.map((point, index) => ({
          ...point,
          order: index,
          timeFrom: this.convertMillisToTimeSpan(point.timeFrom),
          timeTo: this.convertMillisToTimeSpan(point.timeTo)
        }))

        if (this.editMode) {
          await Service.put('road/update', {
            roadId: this.$route.params.roadId,
            points: pointsToSend
          })
        } else {
          await Service.post('road/add', {
            vehicleId: this.vehicleId,
            points: pointsToSend
          })
        }
        this.$router.push({
          name: 'DetailsFleetVehicle',
          params: {
            id: this.vehicleId
          }
        })
      } catch (e) {} finally {
        this.loading = false
      }
    },
    deletePoint (item) {
      const index = this.points.indexOf(item)
      this.points.splice(index, 1)
    },
    addPoint () {
      const maxOrder = this.points.length > 0
        ? Math.max(...this.points.map(p => p.order))
        : -1

      this.points.push({
        location: {
          country: null,
          location: {}
        },
        type: 0,
        order: maxOrder + 1,
        dateTime: null,
        description: null,
        showTimeSliders: false,
        timeFrom: null,
        timeTo: null,
        fix: null,
        weight: null,
        loadMeters: null,
        dirtTime: false
      })
    }
  }
}
</script>

<style scoped>
.v-messages {
    text-align: left !important;
}

.buttonMove {
    padding: 10px 10px !important;
}
</style>
